import React from 'react';

import LoadingSpinnerLong from '../../../molecules/LoadingSpinnerLong/LoadingSpinnerLong.react';
import EmptyTable from '../EmptyTable/EmptyTable.react';

import DataTableRow from './DataTableRow';
import type {DataTableBodyProps} from './DataTable.types';

/**
 * Renders the `<tbody>` for DataTable.
 *
 * Handles three states:
 * 1. **Loading** – full-table spinner (initial fetch)
 * 2. **Reloading** – semi-transparent overlay spinner (background refetch)
 * 3. **Data / Empty** – rows or empty-state fallback
 *
 * Uses the existing `data-table__*` CSS classes for visual parity.
 */
function DataTableBody<TData>({
  table,
  isLoading,
  isReloading,
  emptyState,
  columnCount
}: DataTableBodyProps<TData>) {
  if (isLoading) {
    return (
      <tbody>
        <tr>
          <td className='data-table__loading' colSpan={columnCount}>
            <LoadingSpinnerLong />
          </td>
        </tr>
      </tbody>
    );
  }

  const rows = table.getRowModel().rows;
  const isEmpty = rows.length === 0;
  const emptyContent = emptyState ?? <EmptyTable />;

  return (
    <tbody>
      {isReloading && (
        <tr className='data-table__reloading-indicator' role='presentation'>
          <td className='p-x2' colSpan={columnCount}>
            <LoadingSpinnerLong />
          </td>
        </tr>
      )}

      {isEmpty && !isReloading
        ? emptyContent
        : rows.map((row) => <DataTableRow key={row.id} row={row} />)}
    </tbody>
  );
}

export default DataTableBody;
